import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import { StatusBadge } from "@/components/ui/status-badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { acquisitionsApi, type PurchaseOrderDetail, type POLine } from "@/lib/api/acquisitions";
import { Checkbox } from "@/components/ui/checkbox";
import { poStatusMap } from "./PurchaseOrdersPage";
import { invoiceStatusMap } from "./AcqInvoicesPage";
import {
  Loader2,
  Send,
  XCircle,
  PackageCheck,
  FileText,
  Truck,
  Library,
  Receipt,
} from "lucide-react";
import { BusyButton } from "@/components/acquisitions/BusyButton";
import { useToast } from "@/contexts/ToastContext";
import { getApiErrorMessage } from "@/lib/api-error";

function lineNeedsCatalog(line: POLine) {
  return (
    line.catalogAtReceive &&
    line.quantityReceived > 0 &&
    line.quantityReceived > (line.catalogedQuantity ?? 0)
  );
}

export default function PurchaseOrderDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const [order, setOrder] = useState<PurchaseOrderDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState<string | null>(null);
  const [receiveOpen, setReceiveOpen] = useState(false);
  const [receiveNotes, setReceiveNotes] = useState("");
  const [receiveLines, setReceiveLines] = useState<
    { poLineId: string; quantityReceived: string; conditionNotes: string }[]
  >([]);
  const [catalogLineIds, setCatalogLineIds] = useState<Set<string>>(new Set());

  const loadOrder = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const data = await acquisitionsApi.orders.getById(id);
      setOrder(data);
      setReceiveLines(
        data.lines
          .filter((l) => l.quantityReceived < l.quantity)
          .map((l) => ({
            poLineId: l.id,
            quantityReceived: String(l.quantity - l.quantityReceived),
            conditionNotes: "",
          }))
      );
      setCatalogLineIds(new Set(data.lines.filter(lineNeedsCatalog).map((l) => l.id)));
    } catch {
      toast.error("Failed to load order.");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void loadOrder();
  }, [loadOrder]);

  const handleAction = async (key: string, action: () => Promise<unknown>, msg: string) => {
    setPending(key);
    try {
      await action();
      toast.success(msg);
      loadOrder();
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, "Action failed."));
    } finally {
      setPending(null);
    }
  };

  const handleReceive = async () => {
    if (!id) return;
    const validLines: {
      poLineId: string;
      quantityReceived: number;
      conditionNotes?: string;
    }[] = [];

    for (const rl of receiveLines) {
      const qty = parseInt(rl.quantityReceived, 10);
      if (Number.isNaN(qty) || qty <= 0) continue;
      const poLine = order?.lines.find((l) => l.id === rl.poLineId);
      if (!poLine) continue;
      const rem = poLine.quantity - poLine.quantityReceived;
      if (qty > rem) {
        toast.error(
          `"${poLine.title}": cannot receive ${qty} — only ${rem} remaining on this line.`
        );
        return;
      }
      validLines.push({
        poLineId: rl.poLineId,
        quantityReceived: qty,
        conditionNotes: rl.conditionNotes || undefined,
      });
    }

    if (validLines.length === 0) {
      toast.error("Enter at least one quantity to receive (use “Quantity in this shipment”).");
      return;
    }

    await handleAction(
      "receive",
      () =>
        acquisitionsApi.orders.receive(id, { notes: receiveNotes || undefined, lines: validLines }),
      "Shipment recorded"
    );
    setReceiveOpen(false);
    setReceiveNotes("");
  };

  const formatCurrency = (n: number) =>
    new Intl.NumberFormat("en-NG", { style: "currency", currency: "NGN" }).format(n);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!order) {
    return <div className="py-20 text-center text-slate-500">Order not found</div>;
  }

  const busy = pending !== null;
  const cfg = poStatusMap[order.status] || { variant: "neutral" as const, label: order.status };
  const canSend = order.status === "DRAFT";
  const hasOutstandingReceipt = order.lines.some((l) => l.quantityReceived < l.quantity);
  const canReceive =
    hasOutstandingReceipt &&
    (order.status === "SENT" ||
      order.status === "PARTIALLY_RECEIVED" ||
      order.status === "INVOICED" ||
      order.status === "CLOSED");
  const hasPostedInvoice = order.invoices.some(
    (invoice) => invoice.status === "MATCHED" || invoice.status === "PAID"
  );
  const canCancel =
    (order.status === "DRAFT" || order.status === "SENT") &&
    !order.lines.some((line) => line.quantityReceived > 0) &&
    !hasPostedInvoice;
  const hasUncatalogedReceived = order.lines.some(lineNeedsCatalog);

  const eligibleCatalogIds = order.lines.filter(lineNeedsCatalog).map((l) => l.id);
  const allEligibleCatalogSelected =
    eligibleCatalogIds.length > 0 && eligibleCatalogIds.every((lid) => catalogLineIds.has(lid));

  const toggleCatalogSelectAll = () => {
    if (eligibleCatalogIds.length === 0) return;
    if (allEligibleCatalogSelected) {
      setCatalogLineIds(new Set());
    } else {
      setCatalogLineIds(new Set(eligibleCatalogIds));
    }
  };

  const toggleOneCatalogLine = (lineId: string) => {
    const line = order.lines.find((l) => l.id === lineId);
    if (!line || !lineNeedsCatalog(line)) return;
    setCatalogLineIds((prev) => {
      const next = new Set(prev);
      if (next.has(lineId)) next.delete(lineId);
      else next.add(lineId);
      return next;
    });
  };

  const handleCatalogSelected = async () => {
    if (!id) return;
    const ids = eligibleCatalogIds.filter((lid) => catalogLineIds.has(lid));
    if (ids.length === 0) {
      toast.error("Select at least one line that still needs cataloging.");
      return;
    }
    setPending("catalog");
    try {
      const result = await acquisitionsApi.orders.catalog(id, { lineIds: ids });
      const waiting = result.cataloged.filter((line) => line.deferred);
      const available = result.cataloged.filter((line) => !line.deferred);
      if (waiting.length > 0) {
        toast.success(
          "Copies are waiting in Acquisition cataloging until staff finish subjects, call number, and shelving."
        );
      }
      if (available.length > 0) {
        toast.success(
          "Copies were added to the catalog as available. They show in Acquisition cataloging only when Hand off to cataloging first is turned on for the line."
        );
      }
      if (result.failed.length > 0) {
        toast.error(
          result.failed
            .map((line) => `${line.title}: ${line.error || "Could not create the record"}`)
            .join(" ")
        );
      }
      loadOrder();
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, "Could not create catalog records."));
    } finally {
      setPending(null);
    }
  };

  const totalValue = order.lines.reduce((s, l) => s + Number(l.unitPrice) * l.quantity, 0);

  return (
    <PageContainer width="wide">
      <PageHeader
        title={`PO ${order.orderNumber}`}
        description={`Vendor: ${order.vendor.name}`}
        breadcrumbItems={[
          { label: "Orders", href: "/acquisitions/orders" },
          { label: order.orderNumber },
        ]}
        showSearch={false}
        titleSize="md"
        actions={
          <Button
            variant="outline"
            onClick={() =>
              navigate(
                `/acquisitions/invoices/new?vendorId=${encodeURIComponent(order.vendorId)}&poId=${encodeURIComponent(order.id)}`
              )
            }
          >
            <Receipt className="mr-2 h-4 w-4" />
            Record vendor invoice
          </Button>
        }
      />

      <div className="flex flex-wrap items-center gap-3">
        <StatusBadge variant={cfg.variant}>{cfg.label}</StatusBadge>
        <span className="text-sm text-slate-500">Total: {formatCurrency(totalValue)}</span>
        {order.sentAt && (
          <span className="text-sm text-slate-500">
            Sent: {new Date(order.sentAt).toLocaleDateString()}
          </span>
        )}
      </div>

      {/* Action buttons */}
      <div className="flex flex-wrap gap-2">
        {canSend && (
          <BusyButton
            busy={pending === "send"}
            disabled={busy}
            icon={<Send className="mr-2 h-4 w-4" />}
            onClick={() =>
              handleAction(
                "send",
                () => acquisitionsApi.orders.send(order.id),
                "Order sent — encumbrances created"
              )
            }
          >
            Send Order
          </BusyButton>
        )}
        {canReceive && (
          <Button onClick={() => setReceiveOpen(true)} disabled={busy}>
            <PackageCheck className="mr-2 h-4 w-4" />
            Record Shipment
          </Button>
        )}
        {hasUncatalogedReceived && (
          <BusyButton
            variant="outline"
            busy={pending === "catalog"}
            disabled={busy}
            icon={<Library className="mr-2 h-4 w-4" />}
            onClick={() => handleCatalogSelected()}
          >
            Create catalog records (selected lines)
          </BusyButton>
        )}
        {canCancel && (
          <BusyButton
            variant="destructive"
            busy={pending === "cancel"}
            disabled={busy}
            icon={<XCircle className="mr-2 h-4 w-4" />}
            onClick={() =>
              handleAction(
                "cancel",
                () => acquisitionsApi.orders.cancel(order.id),
                "Order cancelled"
              )
            }
          >
            Cancel
          </BusyButton>
        )}
      </div>

      {/* Order Lines */}
      <div className="rounded-lg border bg-white">
        <div className="border-b px-4 py-3">
          <h3 className="font-semibold text-slate-900">Order Lines</h3>
          {hasUncatalogedReceived && (
            <p className="mt-1 text-xs text-slate-500">
              Use the checkboxes to choose which lines to include in the next catalog run.
            </p>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-slate-50 text-left">
                <th className="w-10 px-2 py-2 text-center">
                  {hasUncatalogedReceived ? (
                    <Checkbox
                      checked={allEligibleCatalogSelected}
                      onCheckedChange={toggleCatalogSelectAll}
                      aria-label="Select all lines for cataloging"
                    />
                  ) : (
                    <span className="text-slate-300"> </span>
                  )}
                </th>
                <th className="px-4 py-2 font-medium text-slate-600">Title</th>
                <th className="px-4 py-2 font-medium text-slate-600">ISBN</th>
                <th className="px-4 py-2 font-medium text-slate-600 text-right">Qty</th>
                <th className="px-4 py-2 font-medium text-slate-600 text-right">Received</th>
                <th className="px-4 py-2 font-medium text-slate-600 text-right">Cataloged</th>
                <th className="px-4 py-2 font-medium text-slate-600 text-right">Unit Price</th>
                <th className="px-4 py-2 font-medium text-slate-600 text-right">Line Total</th>
                <th className="px-4 py-2 font-medium text-slate-600">Fund</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {order.lines.map((line) => {
                const eligible = lineNeedsCatalog(line);
                return (
                  <tr key={line.id}>
                    <td className="px-2 py-2 text-center align-middle">
                      {eligible ? (
                        <Checkbox
                          checked={catalogLineIds.has(line.id)}
                          onCheckedChange={() => toggleOneCatalogLine(line.id)}
                          aria-label={`Include ${line.title} in catalog run`}
                        />
                      ) : (
                        <span className="text-slate-300">—</span>
                      )}
                    </td>
                    <td className="px-4 py-2 font-medium">
                      <div>{line.title}</div>
                      {line.deferCatalogEnrichment && (
                        <span className="text-[10px] font-normal uppercase tracking-wide text-amber-700">
                          Cataloging handoff
                        </span>
                      )}
                      {line.biblioId && (
                        <button
                          type="button"
                          onClick={() => navigate(`/catalog/physical/${line.biblioId}`)}
                          className="mt-1 block text-left text-xs font-normal text-primary hover:underline"
                        >
                          Open in catalog
                        </button>
                      )}
                    </td>
                    <td className="px-4 py-2 text-slate-500 font-mono text-xs">
                      {line.isbn || "—"}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums">{line.quantity}</td>
                    <td className="px-4 py-2 text-right tabular-nums">
                      <span
                        className={
                          line.quantityReceived >= line.quantity
                            ? "text-emerald-600 font-semibold"
                            : line.quantityReceived > 0
                              ? "text-amber-600"
                              : "text-slate-400"
                        }
                      >
                        {line.quantityReceived}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums text-slate-600">
                      {line.catalogAtReceive ? (
                        <>
                          <span className="tabular-nums">{line.catalogedQuantity ?? 0}</span>
                          <span className="text-slate-400"> / {line.quantityReceived}</span>
                        </>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums">
                      {formatCurrency(Number(line.unitPrice))}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums font-medium">
                      {formatCurrency(Number(line.unitPrice) * line.quantity)}
                    </td>
                    <td className="px-4 py-2 text-slate-500">{line.fund?.name || "—"}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="border-t bg-slate-50">
                <td className="px-4 py-2 font-semibold" colSpan={7}>
                  Total
                </td>
                <td className="px-4 py-2 text-right tabular-nums font-bold">
                  {formatCurrency(totalValue)}
                </td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* Shipments */}
      {order.shipments.length > 0 && (
        <div className="rounded-lg border bg-white">
          <div className="border-b px-4 py-3">
            <h3 className="font-semibold text-slate-900">Shipments ({order.shipments.length})</h3>
          </div>
          <div className="divide-y">
            {order.shipments.map((s) => (
              <div key={s.id} className="px-4 py-3">
                <div className="flex items-center gap-3 mb-2">
                  <Truck className="h-4 w-4 text-blue-500" />
                  <span className="text-sm font-medium">
                    Received {new Date(s.receivedAt).toLocaleDateString()}
                  </span>
                  {s.notes && <span className="text-xs text-slate-500">— {s.notes}</span>}
                </div>
                <div className="ml-7 text-xs text-slate-600 space-y-1.5">
                  {s.lines.map((sl) => {
                    const poLine = order.lines.find((l) => l.id === sl.poLineId);
                    return (
                      <div
                        key={sl.id}
                        className="rounded border border-slate-100 bg-slate-50/80 px-2 py-1.5"
                      >
                        <div className="font-medium text-slate-800">
                          {poLine?.title ?? "Line"}:{" "}
                          <span className="tabular-nums text-slate-900">{sl.quantityReceived}</span>{" "}
                          <span className="font-normal text-slate-600">in this shipment</span>
                        </div>
                        {sl.conditionNotes ? (
                          <p className="mt-0.5 text-[11px] text-slate-500">
                            Condition / notes: {sl.conditionNotes}
                          </p>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Linked Invoices */}
      {order.invoices.length > 0 && (
        <div className="rounded-lg border bg-white">
          <div className="border-b px-4 py-3">
            <h3 className="font-semibold text-slate-900">Invoices</h3>
          </div>
          <div className="divide-y">
            {order.invoices.map((inv) => {
              const invCfg = invoiceStatusMap[inv.status] || {
                variant: "neutral" as const,
                label: inv.status,
              };
              return (
                <div key={inv.id} className="flex items-center justify-between px-4 py-3">
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-slate-400" />
                    <span className="text-sm font-mono">{inv.invoiceNumber}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium">
                      {formatCurrency(Number(inv.totalAmount))}
                    </span>
                    <StatusBadge variant={invCfg.variant}>{invCfg.label}</StatusBadge>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Receive Dialog */}
      <Dialog open={receiveOpen} onOpenChange={setReceiveOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Record Shipment</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            {receiveLines.map((rl, idx) => {
              const poLine = order.lines.find((l) => l.id === rl.poLineId);
              if (!poLine) return null;
              const remaining = poLine.quantity - poLine.quantityReceived;
              return (
                <div key={rl.poLineId} className="rounded border p-3 space-y-2">
                  <p className="text-sm font-medium">{poLine.title}</p>
                  <p className="text-xs text-slate-500">
                    Ordered: {poLine.quantity} · Already on PO: {poLine.quantityReceived} · Still to
                    receive: <span className="font-semibold text-slate-700">{remaining}</span>
                  </p>
                  <p className="text-[11px] text-amber-800/90 bg-amber-50 border border-amber-100 rounded px-2 py-1">
                    Enter how many copies arrive <strong>in this delivery</strong> below. Remaining
                    after this shipment will be {remaining} minus that number. Do not put quantities
                    in &quot;Condition&quot; — that field is only for damage or other notes.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-1 sm:col-span-1">
                      <Label className="text-xs font-semibold" required>
                        Quantity in this shipment
                        <span className="font-normal text-slate-500"> (max {remaining})</span>
                      </Label>
                      <Input
                        type="number"
                        min="0"
                        max={remaining}
                        value={rl.quantityReceived}
                        onChange={(e) => {
                          const updated = [...receiveLines];
                          updated[idx] = { ...rl, quantityReceived: e.target.value };
                          setReceiveLines(updated);
                        }}
                        className="text-base tabular-nums"
                        placeholder="0"
                      />
                    </div>
                    <div className="space-y-1 sm:col-span-1">
                      <Label className="text-xs">Condition / damage notes (optional)</Label>
                      <Input
                        value={rl.conditionNotes}
                        onChange={(e) => {
                          const updated = [...receiveLines];
                          updated[idx] = { ...rl, conditionNotes: e.target.value };
                          setReceiveLines(updated);
                        }}
                        placeholder="e.g. 2 jackets torn — not a quantity"
                      />
                    </div>
                  </div>
                </div>
              );
            })}
            <div className="space-y-1.5">
              <Label>Shipment Notes</Label>
              <Textarea
                value={receiveNotes}
                onChange={(e) => setReceiveNotes(e.target.value)}
                rows={2}
                placeholder="General notes about this shipment"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReceiveOpen(false)}>
              Cancel
            </Button>
            <BusyButton onClick={handleReceive} busy={pending === "receive"} disabled={busy}>
              Record Receipt
            </BusyButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}
